import type { BillingSummary } from './dto.ts';

/**
 * The console's side of a billing suspension (ADR-0083, amendment).
 *
 * When the trial runs out or the subscription ends, the server keeps the
 * session but answers 403 `account_suspended` to everything except a person's
 * own sign-in and, for owners, Billing. These are the pieces the Restore
 * access screen needs that can be tested without rendering it. None of them is
 * the boundary: the server's allow-list is.
 */

/** Whether a refused call's body is the suspension, rather than a missing capability. */
export const isAccountSuspended = (body: unknown): boolean =>
  typeof body === 'object' &&
  body !== null &&
  (body as { error?: unknown }).error === 'account_suspended';

type Listener = () => void;

const listeners = new Set<Listener>();

/**
 * Broadcast when any call is refused for the suspension. An account can be
 * suspended mid-session — the sweep ends a trial on the hour — and every screen
 * would otherwise paint its own error strip over data it can no longer load.
 * The shell listens, asks `/auth/me` again, and draws Restore access instead.
 */
export const accountSuspended = (): void => {
  for (const listener of listeners) listener();
};

/** Returns the unsubscribe, so an effect can hand it straight back to React. */
export const onAccountSuspended = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/**
 * Whether this page load is the browser coming back from Stripe's checkout or
 * portal. The API sends it back to `/app?billing=subscribed` or `…=managed`;
 * `cancelled` is somebody who changed their mind, and there is nothing to wait
 * for.
 */
export const returnedFromStripe = (search: string): boolean => {
  const outcome = new URLSearchParams(search).get('billing');
  return outcome === 'subscribed' || outcome === 'managed';
};

export interface SuspensionReason {
  readonly title: string;
  readonly body: string;
}

/**
 * Why the account is suspended, in the terms of what to do about it. Read off
 * the subscription status the summary already carries: a live subscription
 * that is behind is paid through the portal, an ended one or none at all is
 * paid by subscribing.
 */
export const suspensionReason = (summary: Pick<BillingSummary, 'status'>): SuspensionReason => {
  if (summary.status === 'past_due') {
    return {
      title: 'A payment is overdue',
      body:
        'Stripe could not collect the latest invoice, so assessments have stopped. Pay it or ' +
        'update the card under Manage billing and the account is restored within a minute.',
    };
  }
  if (summary.status === 'canceled') {
    return {
      title: 'The subscription has ended',
      body:
        'Assessments have stopped. Subscribe again and the account is restored within a minute, ' +
        'exactly as it was.',
    };
  }
  return {
    title: 'The free trial has ended',
    body:
      'Assessments have stopped. Subscribe and the account is restored within a minute, exactly ' +
      'as it was.',
  };
};
